import React, { useCallback, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  FormProvider,
  Hint,
  Modal,
  Preloader,
  Text,
  useForm,
  useQueryClient,
} from '@common-fe/common-fe';
import _ from 'lodash';

import Permissions from '@/common/permissions';
import { SuccessModal } from '@/components/elements';
import { useHasAccess } from '@/modules/core';

import { QUERY_KEY } from '../../hooks/useGetEnrollment';
import { CIP_SYNTHETIC_ID, EmployeeCIPCloseForm } from '../cip.constants';

import EmployeeCIPCloseContributionForm from './EmployeeCIPCloseContributionForm';
import EmployeeCIPCloseInfo from './EmployeeCIPCloseInfo';
import EmployeeCIPClosePayments from './EmployeeCIPClosePayments';
import { useCloseCIPQuery, useCreateCheckPaymentMethodQuery, useGetTransactionsInfoQuery } from './queries';

interface Props {
  disabled: boolean;
  employeeId: string;
  planId: string;
  planName: string;
}

const EmployeeCIPCloseModalButton: React.FC<Props> = ({ disabled, employeeId, planName, planId }) => {
  const [visible, setVisible] = useState(false);
  const hasAccess = useHasAccess([{ permission: Permissions.MANUAL_ACCOUNT_CLOSURE_CIP_FAILURE }]);

  const {
    isTotalPresent,
    isPendingPresent,
    isLoading: isTransactionLoading,
  } = useGetTransactionsInfoQuery({
    accountId: planId,
  });
  const currentStorageKey = useMemo(() => {
    return `CLOSE_CIP:HIDDEN-${employeeId}/${planId}`;
  }, [employeeId, planId]);
  const [isHidden, setIsHidden] = useState(!!localStorage.getItem(currentStorageKey));
  const [isSuccessModal, setSuccessModal] = useState(false);
  const handleToggleVisible = useCallback(() => {
    setVisible(!visible);
  }, [visible]);

  const methods = useForm<EmployeeCIPCloseForm>({
    defaultValues: {
      contributionTo: 'employee',
    },
  });
  const { isLoading, handleSave } = useCloseCIPQuery();
  const { handleCreate } = useCreateCheckPaymentMethodQuery({
    employeeId,
  });
  const handleSubmit = useCallback(
    async (data: EmployeeCIPCloseForm) => {
      let currentPayment = data.payment;
      if (data.payment === CIP_SYNTHETIC_ID) {
        const response = await handleCreate();
        const id = _.get(response, 'data.id', '') as string;

        if (id) {
          currentPayment = id;
        }
      }
      await handleSave({
        employeeId,
        accountId: planId,
        paymentMethodId: currentPayment,
        returnType: data?.contributionTo,
      });
      setVisible(false);
      setSuccessModal(true);
    },
    [employeeId, handleCreate, handleSave, planId]
  );
  const queryClient = useQueryClient();

  const handleCloseSuccessModal = useCallback(() => {
    setSuccessModal(false);
    setIsHidden(true);
    localStorage.setItem(currentStorageKey, 'true');
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY],
    });
  }, [currentStorageKey, queryClient]);

  if (isHidden) {
    return null;
  }
  if (!hasAccess) {
    return (
      <Hint
        hintWidth="125px"
        hintElement={
          <AppButton
            testId="cip-banner_close"
            onClick={handleToggleVisible}
            disabled
            width="180px"
            color="red"
            buttonType="secondary"
          >
            Close Account
          </AppButton>
        }
      >
        <Text textAlign="center" size="small">
          You don’t have permissions
        </Text>
      </Hint>
    );
  }
  return (
    <>
      <SuccessModal
        testId="cip_close_success"
        visible={isSuccessModal}
        onSetVisible={handleCloseSuccessModal}
        header={`${planName} in now closed`}
        helptext="Account closure may take up to 1 hour."
        buttonText="Got It!"
        buttonStyle={{ width: '200px' }}
        onSubmit={handleCloseSuccessModal}
      />
      <Modal testId="cip_close" visible={visible} onSetVisible={setVisible}>
        {isTransactionLoading ? (
          <Preloader testId="cip_close_modal_loader" />
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <Box>
                <Box direction="column">
                  <Box align="center" margin={{ bottom: 'spacing16' }}>
                    <Text data-testid="cip_close_modal_title" textAlign="center" size="2xl" weight={700}>
                      Close {planName}
                    </Text>
                  </Box>
                </Box>
                <Box round="moduleRound" background="module" pad="spacing24">
                  <Box data-testId="cip_close_modal_info">
                    <EmployeeCIPCloseInfo accountId={planId} />
                  </Box>

                  {(isTotalPresent || isPendingPresent) && (
                    <>
                      <Box
                        data-testId="cip_close_modal_contribution"
                        margin={{
                          top: 'spacing24',
                        }}
                      >
                        <EmployeeCIPCloseContributionForm accountId={planId} />
                      </Box>

                      <Box data-testId="cip_close_modal_payments">
                        <EmployeeCIPClosePayments
                          isPendingPresent={isPendingPresent}
                          employeeId={employeeId}
                        />
                      </Box>
                    </>
                  )}
                </Box>
                <Box direction="row" justify="end" margin={{ top: 'spacing24' }}>
                  <AppButton
                    testId="cip_close_modal_cancel"
                    width="160px"
                    buttonType="secondary"
                    onClick={handleToggleVisible}
                  >
                    Cancel
                  </AppButton>

                  <Box width="spacing12" />
                  <AppButton
                    testId="cip_close_modal_close"
                    width="160px"
                    type="submit"
                    buttonType="primary"
                    color="red"
                  >
                    Close
                  </AppButton>
                </Box>
              </Box>
            </form>
          </FormProvider>
        )}
      </Modal>
      <AppButton
        testId="cip-banner_close"
        onClick={handleToggleVisible}
        disabled={disabled || isLoading}
        width="180px"
        color="red"
        buttonType="secondary"
      >
        {isLoading ? <Preloader /> : 'Close Account'}
      </AppButton>
    </>
  );
};

export default EmployeeCIPCloseModalButton;
